// src/features/leases/components/ProvenanceTooltip.jsx
//
// LSE-TAB-REDESIGN Part 1 — replacement for the single-letter
// ProvenanceBadge. Renders a small colored dot (color encodes source)
// that reveals a tooltip on hover OR keyboard focus with three lines:
//   Source:    Spacebase | Manual | PDF abstraction | Computed
//   Field:     <field name as passed by caller>
//   Imported:  <importedDate> (only when supplied)
//
// Accessibility:
//   - The dot is a real <button type="button"> so it picks up tab
//     focus naturally. Visual styling matches a non-button so it
//     reads as a passive indicator.
//   - aria-describedby points at the tooltip when open.
//   - Tooltip toggles on mouseenter/leave AND focus/blur.
//   - Escape closes the tooltip from keyboard.
//
// The existing ProvenanceBadge is NOT modified — it remains in use by
// the legacy 5-panel layout while Part 3 has not yet rebuilt
// LeaseSummaryTab. Both can coexist; the EditableLeaseField will be
// migrated to ProvenanceTooltip in Part 3.

// INC-03, INC-06, INC-07 (LSE-STYLE-NORMALIZE-1): popup chrome and
// SOURCE_STYLES now route through T. spacebase → T.spacebaseBlue (INC-06),
// computed → T.provenanceComputed (INC-07), popup chrome → T.card/border/
// mid/dark (INC-03). manual and pdf_abstraction remain inline — not yet
// scoped for tokenization; see style guide §11 for tracking.
import React, { useId, useState, useCallback, useRef } from "react";
import { T } from "../../../app/tokens.js";
import { parseDateOnly } from "../leaseTabConfig.js";
import { Tooltip } from "../../../components/Tooltip.jsx";
import { useHoverIntent } from "../../../components/useHoverIntent.js";
import { Popover } from "../../../components/Popover.jsx";

// ITEM 4 (2026-05-21): switched from plain dots to letter-in-circle
// badges per architect direction (Option B). Each badge shows the
// first letter of the source type (S/M/P/C) inside a 12 px filled
// disc — letter + color double-encodes the source for accessibility.
// Colors are the high-contrast palette chosen at the same gate.
const SOURCE_STYLES = {
  spacebase:       { color: T.spacebaseBlue,     letter: "S", label: "Spacebase" },
  manual:          { color: T.provenanceTooltipManual, letter: "M", label: "Manual" },
  pdf_abstraction: { color: T.provenanceTooltipPdf,    letter: "P", label: "PDF abstraction" },
  computed:        { color: T.provenanceComputed, letter: "C", label: "Computed" },
};

function fmtDate(d) {
  if (!d) return null;
  const dt = parseDateOnly(d);
  if (!dt) return String(d);
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// INGEST-POST-APPLY-FIXES: conflict popover — amber dot on M badge when the
// most recent import carried a different value for a manually-set field.
// conflictingImport: { diffId, incomingValue, importedAt, sessionId } | null
// onConflictKeepManual / onConflictUseIncoming: () => void callbacks.

export function ProvenanceTooltip({
  source, field, importedDate,
  conflictingImport = null,
  onConflictKeepManual,
  onConflictUseIncoming,
}) {
  const id = useId();
  const tooltipId = `provenance-tip-${id}`;
  const conflictId = `provenance-conflict-${id}`;
  const triggerRef = useRef(null);
  const intent = useHoverIntent();
  const open = intent.open;
  const [conflictOpen, setConflictOpen] = useState(false);
  const [resolving, setResolving] = useState(false);

  const onKey = useCallback((e) => {
    intent.triggerProps.onKeyDown(e);
    if (e.key === "Escape") { e.preventDefault(); setConflictOpen(false); }
  }, [intent.triggerProps]);

  const style = SOURCE_STYLES[source];
  if (!style) return null;

  const importedFormatted = fmtDate(importedDate);
  const hasConflict = source === "manual" && !!conflictingImport;
  const conflictImportedFormatted = hasConflict ? fmtDate(conflictingImport.importedAt) : null;

  async function handleKeepManual() {
    if (resolving) return;
    setResolving(true);
    try {
      await onConflictKeepManual?.();
    } finally {
      setResolving(false);
      setConflictOpen(false);
    }
  }

  async function handleUseIncoming() {
    if (resolving) return;
    setResolving(true);
    try {
      await onConflictUseIncoming?.();
    } finally {
      setResolving(false);
      setConflictOpen(false);
    }
  }

  return (
    <span style={{ position: "relative", display: "inline-block", lineHeight: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        data-testid={`provenance-tooltip-${source}`}
        data-source-color={style.color}
        data-source-letter={style.letter}
        aria-label={`Source: ${style.label}${field ? `, field ${field}` : ""}${hasConflict ? "- import conflict" : ""}`}
        aria-describedby={open ? tooltipId : undefined}
        {...intent.triggerProps}
        onKeyDown={onKey}
        onClick={hasConflict ? (e) => { e.stopPropagation(); setConflictOpen((v) => !v); intent.dismiss(); } : undefined}
        style={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: style.color,
          border: "none",
          padding: 0,
          margin: 0,
          cursor: hasConflict ? "pointer" : "help",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          verticalAlign: "middle",
          position: "relative",
        }}
      >
        <span
          data-testid={`provenance-letter-${source}`}
          style={{
            fontSize: T.fsMin,
            fontWeight: T.fwBold,
            color: T.white,
            lineHeight: 1,
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          {style.letter}
        </span>
        {/* Amber conflict dot overlaid on the badge */}
        {hasConflict && (
          <span
            data-testid="provenance-conflict-dot"
            style={{
              position: "absolute",
              top: -3,
              right: -3,
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: T.amber,
              border: `1px solid ${T.white}`,
              pointerEvents: "none",
            }}
          />
        )}
      </button>

      {/* Standard provenance tooltip (non-conflict hover) */}
      {open && !conflictOpen && (
        <Tooltip
          id={tooltipId}
          data-testid="provenance-tooltip-popup"
          style={{
            right: 0,
            bottom: "calc(100% + 4px)",
            zIndex: 10,
            textAlign: "left",
          }}
        >
          <span style={{ display: "block", fontWeight: T.fwSemibold, marginBottom: 2 }}>
            Source: {style.label}
          </span>
          {field && (
            <span style={{ display: "block" }}>Field: {field}</span>
          )}
          {importedFormatted && (
            <span style={{ display: "block" }}>Imported: {importedFormatted}</span>
          )}
          {hasConflict && (
            <span style={{ display: "block", color: T.amber, marginTop: 3 }}>
              ● Import conflict - click to review
            </span>
          )}
        </Tooltip>
      )}

      {/* Conflict resolution popover (click-triggered) */}
      {conflictOpen && hasConflict && (
        <Popover
          id={conflictId}
          aria-label="Import conflict"
          data-testid="provenance-conflict-popover"
          triggerRef={triggerRef}
          onClose={() => setConflictOpen(false)}
          style={{
            right: 0,
            bottom: "calc(100% + 4px)",
            border: `1px solid ${T.amber}`,
            whiteSpace: "nowrap",
            zIndex: 20,
            textAlign: "left",
            minWidth: 240,
          }}
        >
          <span style={{ display: "block", fontWeight: T.fwSemibold, marginBottom: 6 }}>
            Import conflict
          </span>
          <span style={{ display: "block", opacity: 0.85, marginBottom: 2 }}>
            Spacebase says:{" "}
            <strong>
              {String(conflictingImport.incomingValue ?? "—")}
            </strong>
            {conflictImportedFormatted && (
              <span style={{ opacity: 0.85 }}> (imported {conflictImportedFormatted})</span>
            )}
          </span>
          <span style={{ display: "block", opacity: 0.85, marginBottom: 10 }}>
            Current value: <strong>{field}</strong> (set manually)
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              data-testid="conflict-keep-manual"
              disabled={resolving}
              onClick={handleKeepManual}
              style={{
                padding: "3px 9px",
                border: "1px solid rgba(255,255,255,0.35)",
                borderRadius: 4,
                background: "transparent",
                color: T.tooltip.fg,
                fontSize: T.fsXxs,
                cursor: "pointer",
                fontWeight: T.fwSemibold,
              }}
            >
              Keep manual
            </button>
            <button
              type="button"
              data-testid="conflict-use-incoming"
              disabled={resolving}
              onClick={handleUseIncoming}
              style={{
                padding: "3px 9px",
                border: "none",
                borderRadius: 4,
                background: T.blue,
                color: T.white,
                fontSize: T.fsXxs,
                cursor: "pointer",
                fontWeight: T.fwSemibold,
              }}
            >
              {resolving ? "…" : "Use Spacebase value"}
            </button>
          </div>
        </Popover>
      )}
    </span>
  );
}
